import { useState } from "react";
import { PageHeader, useResource, friendlyTime } from "@/admin/shared";
import { Button, Field, Input, Select, TextArea, Modal, StatusPill } from "@/admin/ui";
import DataTable from "@/admin/DataTable";
import { api, formatApiError } from "@/admin/api";

const empty = {
    name: "",
    email: "",
    company_id: "",
    company_name: "",
    role: "sales",
    department: "",
    message: "",
};

export default function Invites() {
    const { data, loading, refresh } = useResource("/invites");
    const companies = useResource("/companies");
    const roles = useResource("/roles");
    const depts = useResource("/departments");

    const [open, setOpen] = useState(false);
    const [form, setForm] = useState(empty);
    const [err, setErr] = useState("");
    const [busy, setBusy] = useState(false);

    async function onSend(e) {
        e?.preventDefault?.();
        setBusy(true);
        setErr("");
        try {
            const c = (companies.data || []).find((x) => x.id === form.company_id);
            await api.post("/invites", { ...form, company_name: c?.name || form.company_name });
            setOpen(false);
            setForm(empty);
            await refresh();
        } catch (e2) {
            setErr(formatApiError(e2));
        } finally {
            setBusy(false);
        }
    }

    async function cancel(i) {
        if (!confirm(`Cancel invite for ${i.email}?`)) return;
        try {
            await api.delete(`/invites/${i.id}`);
            await refresh();
        } catch (e) {
            alert(formatApiError(e));
        }
    }

    const cols = [
        {
            key: "name",
            label: "Invitee",
            render: (r) => (
                <div>
                    <div className="font-semibold text-black">{r.name}</div>
                    <div className="text-[12px] text-neutral-500">{r.email}</div>
                </div>
            ),
        },
        { key: "company_name", label: "Company", render: (r) => r.company_name || "—" },
        {
            key: "role",
            label: "Role",
            render: (r) => (
                <span className="text-[11px] font-semibold tracking-[0.16em] uppercase text-violet-700 bg-violet-50 rounded-full px-2.5 py-1">
                    {r.role}
                </span>
            ),
        },
        { key: "department", label: "Department", render: (r) => r.department || "—" },
        { key: "status", label: "Status", render: (r) => <StatusPill status={r.status} /> },
        {
            key: "sent_at",
            label: "Sent",
            render: (r) => <span className="text-[12px] text-neutral-500">{friendlyTime(r.sent_at)}</span>,
        },
        {
            key: "actions",
            label: "",
            render: (r) => (
                <div className="flex justify-end">
                    {r.status === "sent" && (
                        <Button size="sm" variant="danger" onClick={() => cancel(r)} data-testid={`invite-cancel-${r.id}`}>
                            Cancel
                        </Button>
                    )}
                </div>
            ),
            tdClass: "text-right",
        },
    ];

    return (
        <div data-testid="admin-invites-page">
            <PageHeader
                title="Invites"
                subtitle="Send workspace invitations. Recipients set their own password and land in the right dashboard."
                action={
                    <Button size="lg" onClick={() => { setForm(empty); setOpen(true); }} data-testid="invite-create-btn">
                        + Send Invite
                    </Button>
                }
            />

            <DataTable
                cols={cols}
                rows={data || []}
                rowKey={(r) => r.id}
                loading={loading}
                empty={{ title: "No invites yet", hint: "Invite your first company admin or CEO." }}
            />

            <Modal
                open={open}
                onClose={() => setOpen(false)}
                title="Send Invite"
                size="lg"
                testid="invite-modal"
                footer={
                    <>
                        <Button variant="ghost" onClick={() => setOpen(false)}>Cancel</Button>
                        <Button onClick={onSend} disabled={busy} data-testid="invite-send-btn">
                            {busy ? "Sending…" : "Send"}
                        </Button>
                    </>
                }
            >
                <form onSubmit={onSend} className="grid grid-cols-1 sm:grid-cols-2 gap-4">
                    <Field label="Name">
                        <Input required value={form.name} onChange={(e) => setForm({ ...form, name: e.target.value })} data-testid="invite-field-name" />
                    </Field>
                    <Field label="Email">
                        <Input required type="email" value={form.email} onChange={(e) => setForm({ ...form, email: e.target.value })} data-testid="invite-field-email" />
                    </Field>
                    <Field label="Company">
                        <Select value={form.company_id} onChange={(e) => setForm({ ...form, company_id: e.target.value })}>
                            <option value="">— None —</option>
                            {(companies.data || []).map((c) => (
                                <option key={c.id} value={c.id}>{c.name}</option>
                            ))}
                        </Select>
                    </Field>
                    <Field label="Role">
                        <Select value={form.role} onChange={(e) => setForm({ ...form, role: e.target.value })}>
                            {(roles.data || []).map((r) => (
                                <option key={r.id} value={r.key}>{r.name}</option>
                            ))}
                        </Select>
                    </Field>
                    <Field label="Department">
                        <Select value={form.department} onChange={(e) => setForm({ ...form, department: e.target.value })}>
                            <option value="">— None —</option>
                            {(depts.data || []).map((d) => (
                                <option key={d.id} value={d.name}>{d.name}</option>
                            ))}
                        </Select>
                    </Field>
                    <div className="sm:col-span-2">
                        <Field label="Invite Message">
                            <TextArea value={form.message} onChange={(e) => setForm({ ...form, message: e.target.value })} placeholder="You’ve been invited to CEO’s GPT…" />
                        </Field>
                    </div>
                    {err && (
                        <div className="sm:col-span-2 rounded-xl border border-rose-200 bg-rose-50/70 text-rose-700 text-[13px] px-3.5 py-2.5">
                            {err}
                        </div>
                    )}
                </form>
            </Modal>
        </div>
    );
}
